/**
 * Inhalte der Registerkarte „Einfügen“: Hyperlinks, Textmarken und alles, was
 * sich nur im Office-Open-XML-Markup zeigt.
 *
 * Für Deckblätter, Wasserzeichen, Formeln, Spaltenumbrüche und verbundene
 * Tabellenzellen gibt es keine eigene Office.js-Eigenschaft. Sie hinterlassen
 * aber eindeutige Spuren im OOXML, das `getOoxml()` seit WordApi 1.1 liefert:
 *
 * | Funktion            | Spur im OOXML                                   |
 * | ------------------- | ----------------------------------------------- |
 * | Deckblatt           | `<w:docPartGallery w:val="Cover Pages"/>`       |
 * | Wasserzeichen       | Form namens `PowerPlusWaterMarkObject…`         |
 * | Formel (Gleichung)  | `<m:oMath>`                                     |
 * | Spaltenumbruch      | `<w:br w:type="column"/>`                       |
 * | Verbundene Zellen   | `<w:gridSpan …>` bzw. `<w:vMerge …>`            |
 */

import { supportsWordApi, wordApi } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface HyperlinkInfo {
  /** Angezeigter Text des Links. */
  text: string;
  /** Ziel, z. B. "https://…" oder "mailto:…". */
  address: string;
}

/**
 * Alle Hyperlinks im Haupttext. `null`, wenn WordApi 1.3 fehlt.
 */
export async function readHyperlinks(): Promise<HyperlinkInfo[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.hyperlinks)) return null;

  try {
    return await Word.run(async (context) => {
      const ranges = context.document.body.getRange("Whole").getHyperlinkRanges();
      ranges.load("items/text,items/hyperlink");
      await context.sync();
      return ranges.items.map((range) => ({
        text: (range.text ?? "").trim(),
        address: typeof range.hyperlink === "string" ? range.hyperlink.trim() : "",
      }));
    });
  } catch {
    return null;
  }
}

/** Zeigt der Link seine nackte Adresse statt eines sprechenden Textes? */
export function istNackteAdresse(link: HyperlinkInfo): boolean {
  const text = link.text.toLowerCase().replace(/\/$/, "");
  const ziel = link.address.toLowerCase().replace(/^mailto:/, "").replace(/\/$/, "");
  return (
    /^(https?:\/\/|www\.)/.test(text) ||
    text === ziel ||
    text === ziel.replace(/^https?:\/\//, "")
  );
}

/**
 * Namen aller sichtbaren Textmarken im Haupttext. `null`, wenn WordApi 1.4
 * fehlt. Versteckte Textmarken – Word legt sie selbst an, etwa `_Toc…` für
 * das Inhaltsverzeichnis oder `_Ref…` für Querverweise – bleiben außen vor.
 */
export async function readBookmarks(): Promise<string[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.bookmarks)) return null;

  try {
    return await Word.run(async (context) => {
      const result = context.document.body.getRange("Whole").getBookmarks(false, true);
      await context.sync();
      return (result.value ?? []).filter((name) => !name.startsWith("_"));
    });
  } catch {
    return null;
  }
}

/** OOXML des Haupttextes. */
export async function readBodyOoxml(): Promise<string> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const ooxml = context.document.body.getOoxml();
    await context.sync();
    return ooxml.value ?? "";
  });
}

/** OOXML aller Kopfzeilen (Standard, erste Seite, gerade Seiten) aller Abschnitte. */
export async function readHeaderOoxml(): Promise<string> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();

    const typen: Word.HeaderFooterType[] = [
      Word.HeaderFooterType.primary,
      Word.HeaderFooterType.firstPage,
      Word.HeaderFooterType.evenPages,
    ];
    const teile = sections.items.flatMap((section) =>
      typen.map((typ) => section.getHeader(typ).getOoxml()),
    );
    await context.sync();
    return teile.map((teil) => teil.value ?? "").join("\n");
  });
}

/** Deckblatt aus dem Katalog „Einfügen → Deckblatt“? */
export function hatDeckblatt(ooxml: string): boolean {
  return /<w:docPartGallery\s+w:val="Cover Pages"/.test(ooxml);
}

/** Wasserzeichen aus „Entwurf → Wasserzeichen“? */
export function hatWasserzeichen(kopfzeilenOoxml: string): boolean {
  return /PowerPlusWaterMarkObject|WordPictureWatermark/.test(kopfzeilenOoxml);
}

/** Anzahl der Formeln, die über „Einfügen → Formel“ entstanden sind. */
export function anzahlFormeln(ooxml: string): number {
  return (ooxml.match(/<m:oMath[\s>]/g) ?? []).length;
}

/** Anzahl der Spaltenumbrüche („Layout → Umbrüche → Spalte“). */
export function anzahlSpaltenumbrueche(ooxml: string): number {
  return (ooxml.match(/<w:br\b[^>]*w:type="column"/g) ?? []).length;
}

/** Enthält das Markup verbundene Tabellenzellen? */
export function hatVerbundeneZellen(ooxml: string): boolean {
  return /<w:gridSpan\s+w:val="([2-9]|\d{2,})"/.test(ooxml) || /<w:vMerge\b/.test(ooxml);
}
